import type { PersonaModelKey, PersonaName } from "@/types/personas";

export const MODEL_TO_PERSONA: Record<PersonaModelKey, PersonaName> = {
  claude: "delphine",
  gpt: "henri",
  gemini: "akilah",
  perplexity: "julien",
  mistral: "eva",
  deepseek: "ren",
  cohere: "imani",
};

export const PERSONA_TO_MODEL: Record<PersonaName, PersonaModelKey> = Object.fromEntries(
  Object.entries(MODEL_TO_PERSONA).map(([k, v]) => [v, k as PersonaModelKey]),
) as Record<PersonaName, PersonaModelKey>;

const VALID_PERSONAS: ReadonlySet<PersonaName> = new Set(
  Object.values(MODEL_TO_PERSONA),
);

/**
 * Accepte aussi bien :
 *  - une clé modèle ("claude", "gpt", "gemini", "perplexity", "mistral", "deepseek")
 *  - un nom de persona ("delphine", "henri", "akilah", "eva", "ren", "julien")
 *  - une variante de casse ("Henri", "DEEPSEEK", "GPT-5", "claude-opus-4.7")
 *  - un label modèle backend ("gpt-5", "claude-3-5-sonnet", "deepseek-v3", …)
 *
 * Retourne `null` si aucun mapping fiable n'est trouvé — laisser le caller
 * gérer le fallback explicitement plutôt que d'écraser sur "delphine".
 */
export function resolvePersona(raw: string | null | undefined): PersonaName | null {
  if (!raw) return null;
  const norm = String(raw).toLowerCase().trim();
  if (!norm) return null;

  // 1. nom de persona direct
  if (VALID_PERSONAS.has(norm as PersonaName)) return norm as PersonaName;

  // 2. clé modèle exacte
  if (norm in MODEL_TO_PERSONA) return MODEL_TO_PERSONA[norm as PersonaModelKey];

  // 3. label modèle backend qui contient une marque connue
  const marks: Array<[string, PersonaModelKey]> = [
    ["claude", "claude"],
    ["gpt", "gpt"],
    ["openai", "gpt"],
    ["gemini", "gemini"],
    ["perplexity", "perplexity"],
    ["sonar", "perplexity"],
    ["mistral", "mistral"],
    ["deepseek", "deepseek"],
    ["cohere", "cohere"],
    ["command", "cohere"],
  ];
  for (const [needle, key] of marks) {
    if (norm.includes(needle)) return MODEL_TO_PERSONA[key];
  }

  // 4. nom de persona en sous-chaîne (ex: "Henri (CFO)")
  for (const p of VALID_PERSONAS) {
    if (norm.includes(p)) return p;
  }

  return null;
}

export function modelToPersona(model: string): PersonaName {
  return resolvePersona(model) ?? "delphine";
}

export function personaToModel(persona: string): PersonaModelKey {
  return PERSONA_TO_MODEL[persona as PersonaName] ?? "claude";
}
